"use strict";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { UniverseChainId } from "features/chains/types";
import { logger } from "utilities/src/logger/logger";
const DEFAULT_BASE_URL = "https://www.topazdex.com";
const EMPTY_FEE_DETAILS_BY_ADDRESS = /* @__PURE__ */ new Map();
const FIFTEEN_MINUTES_MS = 15 * 60 * 1e3;
const ONE_MINUTE_MS = 60 * 1e3;
const TOPAZ_STATS_API_ENABLED = (process.env.REACT_APP_TOPAZ_STATS_API_ENABLED ?? "true").toLowerCase() !== "false";
function getStatsApiBaseUrl() {
  const base = process.env.REACT_APP_UNISWAP_BASE_API_URL || DEFAULT_BASE_URL;
  return `${base.replace(/\/$/, "")}/api/stats`;
}
async function fetchStats(path) {
  const response = await fetch(`${getStatsApiBaseUrl()}${path}`, {
    method: "GET",
    headers: { Accept: "application/json" }
  });
  if (!response.ok) {
    throw new Error(`Topaz Stats API ${path} failed: HTTP ${response.status}`);
  }
  const envelope = await response.json();
  if (!envelope.ok) {
    throw new Error(`Topaz Stats API ${path} returned ok=false: ${envelope.error?.message ?? "unknown error"}`);
  }
  return envelope.data;
}
function finiteFee(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : void 0;
}
function buildPoolFeeDetails(pool, liveFeePool) {
  const snapshotFeePips = finiteFee(liveFeePool?.snapshotFee) ?? finiteFee(pool.fee);
  const currentFeePips = finiteFee(liveFeePool?.liveFee) ?? snapshotFeePips;
  return {
    currentFeePips,
    snapshotFeePips,
    baseFeePips: finiteFee(liveFeePool?.baseFee),
    maxFeePips: finiteFee(liveFeePool?.maxFee) ?? finiteFee(pool.dynamicFeeCap),
    customFee: liveFeePool?.customFee ?? pool.customFee,
    dynamicFee: liveFeePool?.dynamicFee ?? pool.dynamicFee
  };
}
function buildFeeDetailsByAddress(pools, liveFees) {
  const liveFeesByAddress = new Map(
    (liveFees?.pools ?? []).map((pool) => [pool.poolAddress.toLowerCase(), pool])
  );
  return new Map(
    pools.map((pool) => {
      const poolAddress = pool.poolAddress.toLowerCase();
      return [poolAddress, buildPoolFeeDetails(pool, liveFeesByAddress.get(poolAddress))];
    })
  );
}
export function useTopazPoolFeeDetailsByAddress(chainId, enabled = true) {
  const apiEnabled = TOPAZ_STATS_API_ENABLED && enabled && chainId === UniverseChainId.Mainnet;
  const poolsQuery = useQuery({
    queryKey: ["topaz-pool-fee-details-pools", chainId],
    queryFn: () => fetchStats("/pools?type=all&limit=500"),
    enabled: apiEnabled,
    staleTime: FIFTEEN_MINUTES_MS,
    refetchInterval: FIFTEEN_MINUTES_MS,
    retry: 1
  });
  const liveFeesQuery = useQuery({
    queryKey: ["topaz-pool-fee-details-live", chainId],
    queryFn: () => fetchStats("/live/dynamic-fees"),
    enabled: apiEnabled,
    staleTime: ONE_MINUTE_MS,
    refetchInterval: ONE_MINUTE_MS,
    retry: 1
  });
  useEffect(() => {
    if (liveFeesQuery.isError) {
      logger.warn(
        "useTopazPoolFeeDetailsByAddress.ts",
        "useTopazPoolFeeDetailsByAddress",
        "Falling back to snapshot fees after live fee fetch failed",
        liveFeesQuery.error
      );
    }
  }, [liveFeesQuery.error, liveFeesQuery.isError]);
  return useMemo(() => {
    return {
      feeDetailsByAddress: apiEnabled && poolsQuery.data ? buildFeeDetailsByAddress(poolsQuery.data, liveFeesQuery.data) : EMPTY_FEE_DETAILS_BY_ADDRESS,
      isLoading: apiEnabled && poolsQuery.isLoading
    };
  }, [apiEnabled, liveFeesQuery.data, poolsQuery.data, poolsQuery.isLoading]);
}
